import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "../i18n";
import { api } from "../lib/api";
import safeStorage from "../lib/safeStorage";
import { useAuth } from "../lib/auth";

export default function LanguageSelector({ variant = "navbar" }) {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = SUPPORTED_LANGUAGES.find(l => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  const choose = async (code) => {
    await i18n.changeLanguage(code);
    safeStorage.set("trel-lang", code);
    setOpen(false);
    // Persist to backend if logged in (silent fail — localStorage is the source of truth otherwise)
    if (user) {
      try {
        await api.patch("/auth/language", { preferred_language: code });
      } catch (e) {
        // Non-blocking: localStorage already holds the preference for next page load.
        if (process.env.NODE_ENV !== "production") {
          console.warn("[lang] failed to persist preferred_language", e?.response?.status, e?.message);
        }
      }
    }
  };

  if (variant === "mobile") {
    return (
      <div className="py-2.5 border-b border-[#071A2D]/5" data-testid="lang-mobile">
        <div className="text-xs uppercase tracking-widest text-[#064E3B]/70 mb-2 flex items-center gap-2">
          <Globe size={12}/> {t("nav.language")}
        </div>
        <div className="flex flex-wrap gap-2">
          {SUPPORTED_LANGUAGES.map(l => (
            <button key={l.code} data-testid={`lang-mobile-${l.code}`} onClick={() => choose(l.code)}
              className={`px-3 py-1.5 rounded-full text-xs border ${i18n.language === l.code ? "bg-[#064E3B] text-[#FFF8EE] border-[#064E3B]" : "border-[#071A2D]/15"}`}>
              {l.name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="lang-toggle"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#064E3B]/80 hover:text-[#064E3B] px-2 py-1 rounded-full"
        aria-label={t("nav.language")}
      >
        <Globe size={13}/> <span>{current.short}</span>
      </button>
      {open && (
        <div data-testid="lang-menu" className="absolute right-0 mt-2 w-44 rounded-2xl bg-[#FFF8EE] border border-[#071A2D]/10 shadow-luxury py-2 z-50">
          {SUPPORTED_LANGUAGES.map(l => (
            <button key={l.code} data-testid={`lang-opt-${l.code}`} onClick={() => choose(l.code)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[#064E3B]/5">
              <span>{l.name}</span>
              {i18n.language === l.code && <Check size={14} className="text-[#064E3B]"/>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
